/*!
 * Copyright © 2018. Verizon Connect Ireland Limited. All rights reserved.
 */

import { animate, state, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { environment } from '../../../../../environments/environment';
import { isElementNull } from '../../../../../utils';
import { ENotificationTypes, INotification } from '../models';

const DEFAULT_NOTIFICATION_TEMPORARY_TIMEOUT_MS = '5000';

@Component({
  selector: 'fm-notification',
  animations: [
    trigger('onInteraction', [
      state('in', style({ transform: 'translateX(0px)' })),
      transition('* => in', [style({ transform: 'translateX(345px)' }), animate('192ms')]),

      state('fade', style({ transform: 'translateX(345px)' })),
      transition('* => fade', [style({ transform: 'translateX(0)' }), animate('192ms')]),

      state('panicIn', style({ transform: 'translateY(0px)' })),
      transition('* => in', [style({ transform: 'translateY(5px)' }), animate('192ms')]),

      state('panicFade', style({ transform: 'translateY(5px)' })),
      transition('* => fade', [style({ transform: 'translateY(0)' }), animate('192ms')])
    ])
  ],
  templateUrl: './notification.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NotificationComponent implements OnInit, OnDestroy {
  @Input()
  public set data(notification: INotification) {
    this.isPanic = notification.type === ENotificationTypes.panic;
    this.isDeviceStatus = notification.type === ENotificationTypes.deviceStatusChange;
    this._setInitialAnimation();
    this.notification = notification;
  }

  @Input() set invokeClose(value: boolean) {
    if (value) {
      this.close(null);
    }
  }

  @Output() clicked = new EventEmitter<INotification>();
  @Output() removeNotification = new EventEmitter<INotification>();
  @Output() closeNotification = new EventEmitter<INotification>();

  public dataState: string;
  public isPanic: boolean;
  public isDeviceStatus: boolean;
  public notification: INotification;

  private readonly _timeout: number;
  private _timer: any;
  private readonly _animationSpeed = 192;

  constructor(private readonly _cdr: ChangeDetectorRef) {
    this._timeout = parseInt(environment.config.notificationsTemporaryTimeoutMs || DEFAULT_NOTIFICATION_TEMPORARY_TIMEOUT_MS, 10);
  }

  ngOnInit(): void {
    if (this.notification.temporaryPopup) {
      this._startTimeOut();
    }
  }

  ngOnDestroy(): void {
    clearTimeout(this._timer);
  }

  public onEnter(): void {
    if (this._timer) {
      clearTimeout(this._timer);
    }
  }

  public onLeave(): void {
    if (this.notification.temporaryPopup) {
      clearTimeout(this._timer);
      this._startTimeOut();
    }
  }

  public close(event: Event): void {
    if (!isElementNull(event)) {
      event.stopPropagation();
    }
    this._remove();
    setTimeout(() => {
      this.closeNotification.emit(this.notification);
    }, this._animationSpeed);
  }

  public notificationClicked(data: INotification): void {
    this.clicked.emit(data);
  }

  private _setInitialAnimation(): void {
    if (this.isPanic) {
      this.dataState = 'panicIn';
    } else {
      this.dataState = 'in';
    }
  }

  private _startTimeOut(): void {
    this._timer = setTimeout(() => {
      this._remove();
      setTimeout(() => {
        this.removeNotification.emit(this.notification);
      }, this._animationSpeed);
      this._cdr.markForCheck();
    }, this._timeout);
  }

  private _remove(): void {
    if (this.isPanic) {
      this.dataState = 'panicFade';
    } else {
      this.dataState = 'fade';
    }
  }
}
